/** Fields used for field-aware command-search scoring. */
export interface CommandSearchScoreFields {
    title?: string | null;
    subtitle?: string | null;
    keywords?: Array<string | undefined | null>;
    /** Exact codes / part numbers / order & invoice numbers. */
    identifiers?: Array<string | undefined | null>;
    /** Soft type labels like "Part No", "Invoice", "Navigate". */
    type?: string | null;
    category?: string | null;
}

const WEAK_MATCH_SCORE_THRESHOLD = 25;
const MAX_SUBSTRING_TOKEN_CONTRIBUTION = 40;

/** Punctuation-only or trivial symbol tokens that should not drive ranking. */
const isPunctuationOnlyToken = (token: string): boolean =>
    !/[a-z0-9]/i.test(token);

/** Tokens too weak to score positively on their own (e.g. "&", "/", "-"). */
const isNoiseToken = (token: string): boolean => {
    if (!token) {
        return true;
    }
    if (isPunctuationOnlyToken(token)) {
        return true;
    }
    // Single non-alphanumeric character already covered; also skip lone symbols mixed.
    if (token.length === 1 && !/[a-z0-9]/i.test(token)) {
        return true;
    }
    return false;
};

const normalizeText = (value: string | undefined | null): string =>
    (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const compactIdentifier = (value: string): string =>
    value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Splits a query/text into meaningful tokens, dropping punctuation-only noise.
 * Keeps alphanumerics and hyphenated segments as searchable tokens.
 */
export const tokenizeCommandSearchQuery = (query: string): string[] => {
    const trimmed = normalizeText(query);
    if (!trimmed) {
        return [];
    }
    return trimmed
        .split(/[\s/\\|]+/)
        .map((token) => token.replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, ''))
        .filter((token) => token.length > 0 && !isNoiseToken(token));
};

const fieldTokens = (value: string | undefined | null): string[] =>
    tokenizeCommandSearchQuery(value ?? '');

const scoreTokenAgainstField = (
    token: string,
    fieldText: string,
    fieldWords: string[],
    weights: {exact: number; prefix: number; substring: number},
): number => {
    if (!token || !fieldText) {
        return 0;
    }
    if (fieldWords.some((word) => word === token)) {
        return weights.exact;
    }
    if (fieldWords.some((word) => word.startsWith(token))) {
        return weights.prefix;
    }
    if (fieldText.includes(token)) {
        return weights.substring;
    }
    return 0;
};

const hasConsecutivePhrase = (queryTokens: string[], fieldWords: string[]): boolean => {
    if (queryTokens.length < 2 || fieldWords.length < 2) {
        return false;
    }
    const needle = queryTokens.join(' ');
    return fieldWords.join(' ').includes(needle);
};

/**
 * Field-aware relevance score. Higher is better.
 * Returns 0 when there is no match (except empty query → baseline 1).
 */
export const scoreCommandSearchFields = (
    query: string,
    fields: CommandSearchScoreFields,
): number => {
    const trimmed = normalizeText(query);
    const title = normalizeText(fields.title);
    const subtitle = normalizeText(fields.subtitle);
    const type = normalizeText(fields.type);
    const category = normalizeText(fields.category);
    const keywords = (fields.keywords ?? [])
        .map((keyword) => normalizeText(keyword))
        .filter(Boolean);
    const identifiers = (fields.identifiers ?? [])
        .map((identifier) => normalizeText(identifier))
        .filter(Boolean);

    if (!trimmed) {
        return 1;
    }

    const queryTokens = tokenizeCommandSearchQuery(trimmed);
    const compactQuery = compactIdentifier(trimmed);

    // Pure punctuation / noise queries only score on exact identifier match.
    if (!queryTokens.length) {
        if (compactQuery && identifiers.some((id) => compactIdentifier(id) === compactQuery)) {
            return 150;
        }
        return 0;
    }

    const titleWords = fieldTokens(title);
    const subtitleWords = fieldTokens(subtitle);
    const keywordText = keywords.join(' ');
    const keywordWords = fieldTokens(keywordText);
    const typeWords = fieldTokens(type);
    const categoryWords = fieldTokens(category);
    const identifierCompacts = identifiers.map(compactIdentifier).filter(Boolean);

    let score = 0;
    let matchedTokenCount = 0;
    let substringContribution = 0;

    // Exact / prefix title against full query string.
    if (title && title === trimmed) {
        score += 120;
    } else if (title && title.startsWith(trimmed)) {
        score += 100;
    } else if (titleWords.some((word) => word.startsWith(trimmed))) {
        score += 85;
    }

    // Identifier exact / compact match (part numbers, invoice nos, customer codes).
    if (identifiers.some((id) => id === trimmed)) {
        score += 30;
    } else if (compactQuery && identifierCompacts.some((id) => id === compactQuery)) {
        score += 30;
    } else if (compactQuery && identifierCompacts.some((id) => id.startsWith(compactQuery) && compactQuery.length >= 3)) {
        score += 18;
    }

    queryTokens.forEach((token) => {
        const titleHit = scoreTokenAgainstField(token, title, titleWords, {
            exact: 35,
            prefix: 24,
            substring: 10,
        });
        const subtitleHit = scoreTokenAgainstField(token, subtitle, subtitleWords, {
            exact: 14,
            prefix: 10,
            substring: 4,
        });
        const keywordHit = scoreTokenAgainstField(token, keywordText, keywordWords, {
            exact: 14,
            prefix: 10,
            substring: 4,
        });
        const typeHit = scoreTokenAgainstField(token, type, typeWords, {
            exact: 12,
            prefix: 8,
            substring: 3,
        });
        const categoryHit = scoreTokenAgainstField(token, category, categoryWords, {
            exact: 8,
            prefix: 5,
            substring: 2,
        });
        const identifierHit = identifierCompacts.some((id) => id === compactIdentifier(token))
            ? 28
            : identifierCompacts.some((id) => id.startsWith(compactIdentifier(token)) && token.length >= 2)
                ? 16
                : 0;

        const best = Math.max(titleHit, subtitleHit, keywordHit, typeHit, categoryHit, identifierHit);
        if (best <= 0) {
            return;
        }

        matchedTokenCount += 1;

        // Cap weak substring-only contributions so noisy broad text cannot dominate.
        if (best <= 10) {
            const remaining = Math.max(0, MAX_SUBSTRING_TOKEN_CONTRIBUTION - substringContribution);
            const applied = Math.min(best, remaining);
            substringContribution += applied;
            score += applied;
            return;
        }

        score += best;
    });

    if (matchedTokenCount === queryTokens.length && queryTokens.length > 0) {
        score += 20;
    }

    if (hasConsecutivePhrase(queryTokens, titleWords) || hasConsecutivePhrase(queryTokens, keywordWords)) {
        score += 15;
    }

    // Require at least one meaningful token hit (score may still be from full-title bonuses alone).
    if (score <= 0) {
        return 0;
    }

    return score;
};

/**
 * Scores how well a result matches the query. Higher is more relevant.
 * `parts[0]` is treated as title; remaining parts as keywords.
 * Returns 0 when there is no match (except empty query → baseline 1).
 */
export const scoreCommandSearchQuery = (
    query: string,
    parts: Array<string | undefined | null>,
): number => {
    const [title, ...rest] = parts;
    return scoreCommandSearchFields(query, {
        title,
        keywords: rest,
    });
};

/** Returns whether every meaningful query token appears in the searchable text. */
export const matchesCommandSearchQuery = (
    query: string,
    parts: Array<string | undefined | null>,
): boolean => scoreCommandSearchQuery(query, parts) > 0;

/** True when an item score is considered a weak / noisy match. */
export const isWeakCommandSearchScore = (score: number | undefined): boolean =>
    (score ?? 0) > 0 && (score ?? 0) < WEAK_MATCH_SCORE_THRESHOLD;

/** Threshold used by group weak-match ratio. */
export const COMMAND_SEARCH_WEAK_SCORE_THRESHOLD = WEAK_MATCH_SCORE_THRESHOLD;

/** Sorts command-search results by relevance score (descending), then title. */
export const sortCommandSearchResults = <T extends {score?: number; title?: string}>(items: T[]): T[] =>
    [...items].sort((a, b) => {
        const scoreDelta = (b.score ?? 0) - (a.score ?? 0);
        if (scoreDelta !== 0) {
            return scoreDelta;
        }
        return (a.title ?? '').localeCompare(b.title ?? '');
    });

/** @deprecated Use matchesCommandSearchQuery */
export const matchesPanelCommandQuery = matchesCommandSearchQuery;

/** @deprecated Use scoreCommandSearchQuery */
export const scorePanelCommandQuery = scoreCommandSearchQuery;

/** @deprecated Use sortCommandSearchResults */
export const sortPanelCommandResults = sortCommandSearchResults;
